import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Divider,
    IconButton,
    Stack,
    Typography,
    useTheme
} from "@mui/material";
import {useConfirm} from "material-ui-confirm";
import {useNotification} from "../../../utils/hooks/useNotification";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, {useState} from "react";
import {useRace} from "../../../utils/races/RaceContext";
import {useCategories} from "../../../utils/categories/CategoriesContext";
import useTableParams from "../../../components/list/useTableParams";
import buildUrl from "../../../utils/api/buildUrl";
import useSWR from "swr";
import {BoatInRace} from "../../../schemas/boats/boatInRaceSchema";
import {getFetcher, putFetcher} from "../../../utils/api/fetchers";
import Loading from "../../../components/status/Loading";
import EntityTable, {EntityTableHeaderProps} from "../../../components/list/EntityList";
import EditIcon from "@mui/icons-material/Edit";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import BoatDisqualificationDialog from "../../../components/dialog/BoatDisqualificationDialog";
import BoatDisqualificationNoteDialog from "../../../components/dialog/BoatDisqualificationNoteDialog";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {getCategoryColor} from "../../../utils/categories/categoryColors";

const OverviewClosure: React.FC = () => {
    const confirm = useConfirm();
    const {showNotification} = useNotification();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
//-------------------------------------------------------------------------------------------------------------------------------
    const [disqualificationDialogOpen, setDisqualificationDialogOpen] = useState(false);
    const [editNoteDialogOpen, setEditNoteDialogOpen] = useState(false);
    const [editBoat, setEditBoat] = useState<BoatInRace | null>(null);
//-------------------------------------------------------------------------------------------------------------------------------
    const {race, isLoading: isLoadingRace} = useRace();
    const {categories, isLoading: isLoadingCategories} = useCategories();
//-------------------------------------------------------------------------------------------------------------------------------
    const disqualifiedTableId = "disqualifiedTableId";
    const {
        sortKeys: skDisqualified,
        sortDirections: sdDisqualified,
        filters: fDisqualified
    } = useTableParams(disqualifiedTableId);

    let urlDisqualifiedBoats = buildUrl(`/races/${race?.id}/boats`, {
        sortBy: skDisqualified,
        sortDirections: sdDisqualified,
        filters: fDisqualified
    });
    urlDisqualifiedBoats += `&filter:disqualified=true`;
    const {
        data: disqualifiedBoatsFiltered,
        mutate: mutateRegisteredBoats,
    } = useSWR<BoatInRace[]>(race ? urlDisqualifiedBoats : null, getFetcher, {keepPreviousData: true});
//-------------------------------------------------------------------------------------------------------------------------------
    const urlAllRegisteredBoats = `/races/${race?.id}/boats`;
    const {
        data: allRegisteredBoats,
        mutate: mutateAllRegisteredBoats,
    } = useSWR<BoatInRace[]>(race ? urlAllRegisteredBoats : null, getFetcher, {keepPreviousData: true});
//-------------------------------------------------------------------------------------------------------------------------------

    if (isLoadingRace || !race || isLoadingCategories || !categories) return <Loading/>

    const handleCancelDisqualification = async (boatId: number) => {
        confirm({
            title: "Zrušit diskvalifikaci lodě",
            description: "Opravdu chcete zrušit diskvalifikaci této lodě?",
            confirmationText: "Potvrdit",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        })
            .then(() => {
                putFetcher(`/races/${race.id}/boats/${boatId}/disqualified`, {disqualified: false})
                    .then(() => {
                        showNotification("Lodi byla úspěšně zrušena diskvalifikace", "success");
                        mutateRegisteredBoats();
                        mutateAllRegisteredBoats();
                    })
                    .catch((error) => {
                        showNotification("Chyba při zrušení diskvalifikace lodi", "error");
                        console.error(error);
                    });
            })
            .catch(() => {
            });
    }

    const statisticsContent = (
        <>
            <Typography variant="h5">Statistiky závodu</Typography>
            <Stack direction="column" spacing={1} mt={2} sx={{px: 2}}>
                <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
                    <Typography variant="body1">Celkový počet lodí:</Typography>
                    <Typography variant="body1">{allRegisteredBoats?.length ?? 0}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
                    <Typography variant="body1">Počet diskvalifikovaných lodí:</Typography>
                    <Typography
                        variant="body1">{allRegisteredBoats?.filter((boat) => boat.disqualified === true).length ?? 0}</Typography>
                </Stack>
                <Typography variant="body1" fontWeight="bold" pt={2}>
                    Počty lodí v jednotlivých kategoriích:
                </Typography>
                <Stack direction="column" spacing={1} mt={2} sx={{px: 2}}
                       divider={<Divider orientation="horizontal" flexItem/>}>
                    {categories.map((category) => (
                        <Stack key={category.id} direction="row" justifyContent="space-between"
                               alignItems="center" spacing={2}>
                            <Typography variant="body1">{category.name}:</Typography>
                            <Typography variant="body1">
                                {allRegisteredBoats?.filter(boat => boat.category.id === category.id).length ?? 0}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </>
    );

    const disqualificationContent = (
        <>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5">Diskvalifikované lodě</Typography>
                <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => setDisqualificationDialogOpen(true)}
                    title="Diskvalifikovat loď (zobrazí se v seznamu níže)"
                >
                    Diskvalifikovat loď
                </Button>
            </Stack>
            <EntityTable
                tableId={disqualifiedTableId}
                headers={[
                    {key: 'name', label: 'Jméno', sortable: true, filtered: true},
                    {
                        key: 'category',
                        label: 'Kategorie',
                        sortable: true,
                        filtered: true,
                        filterConfig: {
                            type: 'multiSelect',
                            options: [
                                ...(categories).map(category => ({
                                    label: category.name,
                                    value: category.categoryNumber.toString()
                                }))
                            ]
                        },
                        render: (category: any) => (
                            <span
                                style={{color: getCategoryColor(category?.categoryNumber), fontWeight: "bold"}}
                            >
                                {category?.name}
                            </span>
                        )
                    },
                    race.rules.startNumbers ? {
                        key: 'startNumber',
                        label: 'Startovní číslo',
                        sortable: true,
                        filtered: true,
                        filterConfig: {
                            type: 'number',
                            min: 0
                        },
                        render: (type: number) => type ||
                            <Typography color="textSecondary" fontStyle="italic">Neuvedeno</Typography>
                    } : {
                        key: "boatNumber",
                        label: "Evidenční číslo",
                        sortable: true,
                        filtered: true,
                        filterConfig: {
                            type: 'number',
                            min: 0
                        },
                        render: (type: string) => type ||
                            <Typography color="textSecondary" fontStyle="italic">Neuvedeno</Typography>
                    },
                    {
                        key: 'controls',
                        render:
                            (boat: BoatInRace) => (
                                <Stack direction="row" spacing={1}>
                                    <IconButton
                                        color="primary"
                                        title="Upravit poznámky k diskvalifikaci"
                                        onClick={() => {
                                            if (race.rules.startNumbers && !boat.startNumber) {
                                                showNotification("Loď nemá vyplněné startovní číslo, poznámku nelze upravit.", "info");
                                                return;
                                            }
                                            setEditBoat(boat);
                                            setEditNoteDialogOpen(true);
                                        }}
                                    >
                                        <EditIcon/>
                                    </IconButton>
                                    <IconButton color="error" title="Zrušit diskvalifikaci lodi"
                                                onClick={() => handleCancelDisqualification(boat.boatId)}>
                                        <HighlightOffIcon/>
                                    </IconButton>
                                </Stack>
                            )
                    }
                ] as EntityTableHeaderProps[]}
                entities={disqualifiedBoatsFiltered ?? []}
                paged={false}
                indexes
                entitiesCount={disqualifiedBoatsFiltered?.length ?? 0}
                showLoading={false}
            />
        </>
    );

    return (
        <>
            {isMobile ? (
                <Stack direction="column" spacing={2}>
                    <Accordion defaultExpanded>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon/>}
                            aria-controls="panel-statistics-content"
                            id="panel-statistics-header"
                        >
                            <Typography variant="h6">Statistiky</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {statisticsContent}
                        </AccordionDetails>
                    </Accordion>
                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon/>}
                            aria-controls="panel-disqualification-content"
                            id="panel-disqualification-header"
                        >
                            <Typography variant="h6">Diskvalifikace</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {disqualificationContent}
                        </AccordionDetails>
                    </Accordion>
                </Stack>
            ) : (
                <Stack direction="row" divider={<Divider orientation="vertical" flexItem/>}
                       spacing={2} justifyContent="space-between" overflow="auto">
                    <Stack direction="column" spacing={0} flexGrow={1} width="35%">
                        {statisticsContent}
                    </Stack>
                    <Stack direction="column" spacing={0} flexGrow={1}>
                        {disqualificationContent}
                    </Stack>
                </Stack>
            )}
            <BoatDisqualificationNoteDialog
                open={editNoteDialogOpen}
                onClose={() => {
                    setEditNoteDialogOpen(false);
                    setEditBoat(null);
                }}
                defaultValue={editBoat?.notes ?? undefined}
                onSave={async (value) => {
                    if (!editBoat) return;
                    if (race.rules.startNumbers && !editBoat.startNumber) {
                        showNotification("Loď nemá vyplněné startovní číslo, poznámku nelze uložit.", "info");
                        setEditNoteDialogOpen(false);
                        setEditBoat(null);
                        return;
                    }
                    try {
                        await putFetcher(`/races/${race.id}/boats/${editBoat.boatId}`, {
                            startNumber: editBoat.startNumber ?? null,
                            notes: value
                        });
                        showNotification("Poznámka byla úspěšně upravena.", "success");
                        mutateRegisteredBoats();
                        mutateAllRegisteredBoats();
                    } catch (e) {
                        showNotification("Chyba při ukládání poznámky.", "error");
                        console.error(e);
                    }
                    setEditNoteDialogOpen(false);
                    setEditBoat(null);
                }}
            />
            <BoatDisqualificationDialog
                open={disqualificationDialogOpen}
                onClose={() => setDisqualificationDialogOpen(false)}
                onSuccess={() => {
                    setDisqualificationDialogOpen(false);
                    mutateRegisteredBoats();
                    mutateAllRegisteredBoats();
                }}
            />
        </>
    )
}

export default OverviewClosure;